import { AlertCircle, Loader2, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import manifestService, {
  ChargeType,
  Manifest,
} from '../../services/manifestService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

interface BillingTabProps {
  manifest: Manifest;
  onUpdate: () => void;
  disabled?: boolean;
}

const CHARGE_TYPES: { value: ChargeType; label: string }[] = [
  { value: 'FREIGHT', label: 'Freight' },
  { value: 'HANDLING', label: 'Handling' },
  { value: 'DOCUMENTATION', label: 'Documentation' },
  { value: 'CUSTOMS', label: 'Customs' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'FUEL_SURCHARGE', label: 'Fuel Surcharge' },
  { value: 'OTHER', label: 'Other' },
];

const BillingTab: React.FC<BillingTabProps> = ({
  manifest,
  onUpdate,
  disabled = false,
}) => {
  const [chargeType, setChargeType] = useState<ChargeType>('OTHER');
  const [chargeName, setChargeName] = useState('');
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [vendor, setVendor] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleAddCharge = async () => {
    if (!chargeType || !chargeName || !amount) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in charge type, name, and amount',
        variant: 'destructive',
      });
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum < 0) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid positive amount',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await manifestService.addCharge(manifest._id, {
        chargeType,
        chargeName,
        amount: amountNum,
        remark,
        vendor,
        company,
      });

      // Reset form
      setChargeType('OTHER');
      setChargeName('');
      setAmount('');
      setRemark('');
      setVendor('');
      setCompany('');

      toast({
        title: 'Success',
        description: 'Charge added successfully',
      });

      onUpdate();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add charge',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCharge = async (chargeId: string) => {
    setDeletingId(chargeId);
    try {
      await manifestService.deleteCharge(manifest._id, chargeId);
      toast({
        title: 'Success',
        description: 'Charge deleted successfully',
      });
      onUpdate();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete charge',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className='space-y-6'>
      {/* Add Charge Form */}
      <div className='border rounded-lg p-4'>
        <div className='flex items-center gap-2 mb-4'>
          <Button
            onClick={handleAddCharge}
            disabled={disabled || loading}
            className='bg-green-600 hover:bg-green-700'
          >
            {loading ? (
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
            ) : (
              <Plus className='h-4 w-4 mr-2' />
            )}
            ADD CHARGE
          </Button>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4'>
          <div>
            <Label htmlFor='chargeType'>TYPE</Label>
            <Select
              value={chargeType}
              onValueChange={value => setChargeType(value as ChargeType)}
              disabled={disabled}
            >
              <SelectTrigger className='mt-1'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHARGE_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor='vendor'>VENDOR</Label>
            <Input
              id='vendor'
              placeholder='Vendor...'
              value={vendor}
              onChange={e => setVendor(e.target.value)}
              disabled={disabled}
              className='mt-1'
            />
          </div>

          <div>
            <Label htmlFor='company'>COMPANY</Label>
            <Input
              id='company'
              placeholder='Company...'
              value={company}
              onChange={e => setCompany(e.target.value)}
              disabled={disabled}
              className='mt-1'
            />
          </div>

          <div>
            <Label htmlFor='chargeName'>CHARGE</Label>
            <Input
              id='chargeName'
              placeholder='Charge name...'
              value={chargeName}
              onChange={e => setChargeName(e.target.value)}
              disabled={disabled}
              className='mt-1'
            />
          </div>

          <div>
            <Label htmlFor='amount'>AMOUNT</Label>
            <Input
              id='amount'
              type='number'
              placeholder='0.00'
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={disabled}
              className='mt-1'
            />
          </div>

          <div>
            <Label htmlFor='remark'>REMARK</Label>
            <Input
              id='remark'
              placeholder='Remark...'
              value={remark}
              onChange={e => setRemark(e.target.value)}
              disabled={disabled}
              className='mt-1'
            />
          </div>
        </div>
      </div>

      {/* Charges Table */}
      <div>
        {manifest.charges.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-8 text-muted-foreground border rounded-lg'>
            <AlertCircle className='h-8 w-8 mb-2' />
            <p>No charges added yet</p>
          </div>
        ) : (
          <>
            <div className='overflow-x-auto -mx-4 sm:mx-0'>
            <div className='min-w-[700px] sm:min-w-0 px-4 sm:px-0'>
            <Table>
              <TableHeader>
                <TableRow className='bg-muted/50'>
                  <TableHead>TYPE</TableHead>
                  <TableHead>VENDOR</TableHead>
                  <TableHead>COMPANY</TableHead>
                  <TableHead>CHARGE</TableHead>
                  <TableHead>AMOUNT</TableHead>
                  <TableHead>REMARK</TableHead>
                  <TableHead>ACTION</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {manifest.charges.map(charge => (
                  <TableRow key={charge._id}>
                    <TableCell>{charge.chargeType}</TableCell>
                    <TableCell>{charge.vendor || '-'}</TableCell>
                    <TableCell>{charge.company || '-'}</TableCell>
                    <TableCell>{charge.chargeName}</TableCell>
                    <TableCell className='font-medium'>
                      ₹{charge.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>{charge.remark || '-'}</TableCell>
                    <TableCell>
                      <Button
                        variant='destructive'
                        size='sm'
                        onClick={() => handleDeleteCharge(charge._id)}
                        disabled={disabled || deletingId === charge._id}
                      >
                        {deletingId === charge._id ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <>
                            <Trash2 className='h-4 w-4 mr-1' />
                            DELETE
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            </div>

            {/* Total */}
            <div className='flex justify-end mt-4 p-4 bg-muted/50 rounded-lg'>
              <div className='text-lg font-semibold'>
                Total Charges: ₹{manifest.totalCharges.toFixed(2)}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingTab;
